import { useState } from 'react';
import { useMyJobs } from '@/hooks/useJobs';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { LoadingSpinner } from '@/components/shared/LoadingStates';
import { Link, useNavigate } from 'react-router-dom';
import { 
  MapPin, Clock, Phone, SignOut, Eye, 
  Wrench, ArrowRight, CheckCircle, Hourglass, 
  SpinnerGap, Warning, Package, Star
} from '@phosphor-icons/react';
import { useLogout } from '@/hooks/useAuth';
import { JOB_STATUS, JOB_STATUS_LABELS } from '@/constants/status';
import useAppStore from '@/stores/appStore';

// Status config for visual styling
const STATUS_CONFIG = {
  [JOB_STATUS.PENDING]:     { color: 'bg-amber-100 text-amber-800 border-amber-200',     icon: Hourglass,    dot: 'bg-amber-500' },
  [JOB_STATUS.NAVIGATING]:  { color: 'bg-blue-100 text-blue-800 border-blue-200',        icon: MapPin,       dot: 'bg-blue-500' },
  [JOB_STATUS.ARRIVED]:     { color: 'bg-indigo-100 text-indigo-800 border-indigo-200',  icon: SpinnerGap,   dot: 'bg-indigo-500' },
  [JOB_STATUS.IN_PROGRESS]: { color: 'bg-orange-100 text-orange-800 border-orange-200',  icon: Wrench,       dot: 'bg-orange-500' },
  [JOB_STATUS.COMPLETED]:   { color: 'bg-emerald-100 text-emerald-800 border-emerald-200', icon: CheckCircle, dot: 'bg-emerald-500' },
};

const sortNewestFirst = (arr) =>
  [...arr].sort((a, b) => {
    if (!a.created_at && !b.created_at) return 0;
    if (!a.created_at) return 1;
    if (!b.created_at) return -1;
    return new Date(b.created_at) - new Date(a.created_at);
  });

const JobCard = ({ job, brandColor }) => {
  const isUrgent = job.priority === 'urgent';
  const cfg = STATUS_CONFIG[job.status] || STATUS_CONFIG[JOB_STATUS.PENDING];
  const StatusIcon = cfg.icon;
  const address = job.service_address || job.customer_address;

  const timeLabel = job.created_at
    ? new Date(job.created_at).toLocaleString('en-IN', {
        day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', hour12: true
      })
    : 'Today';

  return (
    <Link to={`/ops/tech/jobs/${job.id}`} className="block group" data-testid={`job-card-${job.id}`}>
      <div
        className="rounded-2xl border bg-white shadow-sm hover:shadow-lg active:scale-[0.98] transition-all duration-200 overflow-hidden"
        style={{ borderLeftColor: isUrgent ? '#ef4444' : brandColor, borderLeftWidth: 4 }}
      >
        {/* Top strip — status + urgency */}
        <div className="flex items-center justify-between px-4 pt-3 pb-0">
          <div className="flex items-center gap-2">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border ${cfg.color}`}>
              <StatusIcon size={11} weight="bold" />
              {JOB_STATUS_LABELS[job.status] || job.status}
            </span>
            {isUrgent && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-red-50 border border-red-300 text-red-700 text-[10px] font-black rounded-full uppercase tracking-wider">
                <Warning size={10} weight="fill" /> Urgent
              </span>
            )}
          </div>
          <span className="text-[10px] font-mono font-bold text-slate-400">{job.ticket_number}</span>
        </div>

        {/* Main content */}
        <div className="px-4 pt-2 pb-3 space-y-3">
          {/* Customer */}
          <div className="flex items-center justify-between gap-2">
            <div className="min-w-0">
              <p className="font-black text-slate-900 text-base leading-tight truncate">
                {job.customer_name}
              </p>
              {job.customer_phone && (
                <a
                  href={`tel:${job.customer_phone}`}
                  onClick={e => e.stopPropagation()}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:underline mt-0.5"
                >
                  <Phone size={11} /> {job.customer_phone}
                </a>
              )}
            </div>
          </div>

          {/* Divider */}
          <div className="border-t border-slate-100" />

          {/* Product + Issue */}
          <div className="flex items-start gap-2.5">
            <div className="p-1.5 rounded-lg shrink-0 mt-0.5" style={{ backgroundColor: `${brandColor}15` }}>
              <Package size={16} style={{ color: brandColor }} />
            </div>
            <div className="min-w-0">
              <p className="font-bold text-sm text-slate-800 leading-snug">{job.product_name}</p>
              <p className="text-xs text-slate-500 font-semibold mt-0.5 truncate">{job.issue_category}</p>
            </div>
          </div>

          {/* Address */}
          {address ? (
            <div className="flex items-start gap-2 text-xs text-slate-600 bg-slate-50 rounded-xl px-3 py-2">
              <MapPin size={14} className="mt-0.5 shrink-0 text-slate-400" />
              <span className="line-clamp-2 leading-relaxed">{address}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2 text-xs text-slate-400 italic">
              <MapPin size={13} /> No address provided
            </div>
          )}

          {/* Footer */}
          <div className="flex items-center justify-between pt-1">
            <div className="flex items-center gap-1.5 text-[11px] text-slate-400 font-semibold">
              <Clock size={13} />
              <span>{timeLabel}</span>
            </div>
            <span
              className="flex items-center gap-1 text-[11px] font-black uppercase tracking-wider transition-colors group-hover:translate-x-0.5 duration-150"
              style={{ color: brandColor }}
            >
              Open Job <ArrowRight size={12} weight="bold" />
            </span>
          </div>
        </div>
      </div>
    </Link>
  );
};

const EmptyState = ({ label }) => (
  <div className="flex flex-col items-center justify-center py-16 text-center">
    <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mb-4">
      <Wrench size={30} className="text-slate-300" />
    </div>
    <p className="text-sm font-bold text-slate-400">No {label} jobs</p>
    <p className="text-xs text-slate-300 mt-1">Check back soon</p>
  </div>
);

export const TechnicianDashboard = () => {
  const { data: jobs, isLoading } = useMyJobs();
  const logout = useLogout();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('pending');

  const viewAsRole = useAppStore((state) => state.viewAsRole);
  const viewAsUser = useAppStore((state) => state.viewAsUser);
  const clearViewAs = useAppStore((state) => state.clearViewAs);
  const currentUser = useAppStore((state) => state.currentUser);
  const tenantConfig = useAppStore((state) => state.tenantConfig);
  const brandColor = tenantConfig?.brand_colour || '#002FA7';

  const isImpersonated = currentUser?.role === 'super_admin' && viewAsRole === 'technician';

  // Display name: when impersonating, show the technician's name; otherwise own name
  const displayUser = isImpersonated ? viewAsUser : currentUser;
  const firstName = displayUser?.full_name?.split(' ')[0] || 'Technician';

  const handleExitViewAs = () => {
    clearViewAs();
    navigate('/ops/admin');
  };

  const allJobs = jobs || [];
  const pendingJobs = sortNewestFirst(allJobs.filter(j =>
    j.status === JOB_STATUS.PENDING || j.status === JOB_STATUS.NAVIGATING || j.status === JOB_STATUS.ARRIVED
  ));
  const inProgressJobs = sortNewestFirst(allJobs.filter(j => j.status === JOB_STATUS.IN_PROGRESS));
  const completedJobs = sortNewestFirst(allJobs.filter(j => j.status === JOB_STATUS.COMPLETED));

  const TABS = [
    { key: 'pending',     label: 'Pending',    count: pendingJobs.length,     accentColor: 'amber' },
    { key: 'in_progress', label: 'Active',      count: inProgressJobs.length,  accentColor: 'orange' },
    { key: 'completed',   label: 'Done',        count: completedJobs.length,   accentColor: 'emerald' },
  ];

  const TAB_JOBS = {
    pending:     pendingJobs,
    in_progress: inProgressJobs,
    completed:   completedJobs,
  };

  const ACCENT = {
    amber:   { ring: 'ring-amber-400',   bg: 'bg-amber-500',   text: 'text-amber-700',   light: 'bg-amber-50' },
    orange:  { ring: 'ring-orange-400',  bg: 'bg-orange-500',  text: 'text-orange-700',  light: 'bg-orange-50' },
    emerald: { ring: 'ring-emerald-400', bg: 'bg-emerald-500', text: 'text-emerald-700', light: 'bg-emerald-50' },
  };

  return (
    <div className="min-h-screen pb-24" style={{ background: 'linear-gradient(160deg, #f8faff 0%, #f0f4ff 100%)' }}>

      {/* Impersonation bar */}
      {isImpersonated && (
        <div className="bg-amber-500 text-white px-4 py-2.5 flex items-center justify-between text-xs sm:text-sm font-semibold border-b border-amber-600">
          <div className="flex items-center gap-2">
            <Eye size={18} />
            <span>Viewing as Technician: {viewAsUser?.full_name}</span>
          </div>
          <button
            onClick={handleExitViewAs}
            className="bg-white text-amber-700 px-3 py-1 rounded text-xs font-bold hover:bg-amber-50 transition-colors shrink-0"
          >
            Exit View-As
          </button>
        </div>
      )}

      {/* ─── Header ─────────────────────────────────────── */}
      <header className="relative text-white" style={{ background: `linear-gradient(135deg, ${brandColor}f0 0%, ${brandColor}b0 100%)` }}>
        {/* Decorative circles */}
        <div className="absolute -right-8 -top-8 w-40 h-40 rounded-full bg-white/10 pointer-events-none" />
        <div className="absolute -left-4 bottom-[-24px] w-28 h-28 rounded-full bg-white/10 pointer-events-none" />

        <div className="relative z-10 px-5 pt-6 pb-14 flex items-start justify-between">
          <div>
            <p className="text-xs font-bold uppercase tracking-widest text-white/60 mb-1">Field Technician</p>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight leading-none mb-1">
              Hello, {firstName} 🛠️
            </h1>
            <p className="text-white/70 text-xs font-semibold mt-1">
              {pendingJobs.length + inProgressJobs.length > 0
                ? `${pendingJobs.length + inProgressJobs.length} active assignment${pendingJobs.length + inProgressJobs.length !== 1 ? 's' : ''} today`
                : 'No active assignments — great work!'}
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={logout}
            className="text-white hover:bg-white/15 mt-1"
            data-testid="logout-btn"
          >
            <SignOut size={22} />
          </Button>
        </div>
      </header>

      {/* ─── Stat Cards (overlap header bottom) ─── */}
      <div className="px-4 -mt-8 relative z-10">
        <div className="grid grid-cols-3 gap-3">
          {TABS.map(tab => {
            const acc = ACCENT[tab.accentColor];
            const isActive = activeTab === tab.key;
            return (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key)}
                className={`rounded-2xl p-3.5 text-center shadow-md transition-all duration-200 ${
                  isActive
                    ? `bg-white ring-2 ${acc.ring} shadow-lg scale-105`
                    : 'bg-white hover:scale-[1.02]'
                }`}
              >
                <p className={`text-2xl font-black ${isActive ? acc.text : 'text-slate-700'}`}>{tab.count}</p>
                <p className={`text-[10px] font-bold uppercase tracking-wider mt-0.5 ${isActive ? acc.text : 'text-slate-400'}`}>
                  {tab.label}
                </p>
                {isActive && <div className={`h-0.5 rounded-full mt-1.5 mx-auto w-6 ${acc.bg}`} />}
              </button>
            );
          })}
        </div>
      </div>

      {/* ─── Content ─────────────────────────────────────── */}
      <div className="px-4 pt-5 space-y-3">

        {/* Job list */}
        {isLoading ? (
          <LoadingSpinner />
        ) : TAB_JOBS[activeTab].length > 0 ? (
          <div className="space-y-3 pb-6">
            {TAB_JOBS[activeTab].map(job => (
              <JobCard key={job.id} job={job} brandColor={brandColor} />
            ))}
          </div>
        ) : (
          <EmptyState label={TABS.find(t => t.key === activeTab)?.label?.toLowerCase() || ''} />
        )}
      </div>
    </div>
  );
};